/**
 * Ops-facing Leads page. Lists the ops user's assigned campaigns and lets
 * them generate a leads .xlsx for any campaign — defaulting the "as of" date
 * to yesterday IST.
 *
 * Uses the job-based flow so users see live progress logs while large
 * exports (e.g. 400-day lookback) run in the background:
 *    POST /leads/campaigns/{id}/leads-jobs      → { job_id }
 *    GET  /leads/jobs/{job_id}   (polled 1x/sec) → { status, logs, timings, rows }
 *    GET  /leads/jobs/{job_id}/download        → xlsx blob
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import DatePicker from "@/components/common/DatePicker";
import { useAuth } from "@/lib/auth";
import { Download, Tag, Loader2, CheckCircle2, XCircle, RefreshCw, Clock, AlertTriangle } from "lucide-react";

function yesterdayIST() {
  const nowMs = Date.now() + (5 * 60 + 30) * 60 * 1000;
  const d = new Date(nowMs);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

function LogPanel({ job }) {
  const scrollRef = useRef(null);
  const lastLenRef = useRef(0);
  useEffect(() => {
    const len = job?.logs?.length || 0;
    if (scrollRef.current && len !== lastLenRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      lastLenRef.current = len;
    }
  }, [job?.logs?.length]);
  if (!job) return null;
  return (
    // FIXED height (h-72) — using max-h caused the dialog to reflow on every
    // new log line, which made Radix re-anchor the modal → visible flicker.
    <div className="border rounded-lg mt-3 bg-slate-950 text-slate-100 font-mono text-[11px]"
         style={{ contain: "layout paint" }}>
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800">
        <div className="flex items-center gap-2">
          {job.status === "done" && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
          {job.status === "failed" && <XCircle className="w-3 h-3 text-red-400" />}
          {(job.status === "queued" || job.status === "running") &&
            <Loader2 className="w-3 h-3 animate-spin text-blue-400" />}
          <span className="uppercase tracking-wide text-[10px]">
            {job.status} · {job.phase}
          </span>
        </div>
        <span className="text-[10px] text-slate-400">
          {job.rows ? `${job.rows.toLocaleString()} rows` : "—"}
          {job.timings?.total_ms ? ` · ${(job.timings.total_ms / 1000).toFixed(1)}s` : ""}
        </span>
      </div>
      <div ref={scrollRef} className="h-72 overflow-y-auto px-3 py-2 space-y-0.5" data-testid="leads-job-logs">
        {(job.logs || []).map((l, i) => (
          <div key={`${l.ts}-${i}`} className="whitespace-pre-wrap break-words">
            <span className="text-slate-500">{l.ts.slice(11, 23)}</span>{" "}
            <span className={l.msg.startsWith("WARN") ? "text-amber-300" :
                             l.msg.startsWith("FAILED") ? "text-red-300" : ""}>
              {l.msg}
            </span>
          </div>
        ))}
        {(job.logs || []).length === 0 && (
          <div className="text-slate-500 italic">Waiting for logs…</div>
        )}
      </div>
    </div>
  );
}
const MemoLogPanel = React.memo(LogPanel, (prev, next) =>
  // Only re-render when something visually meaningful changed. Prevents
  // 1x/sec parent-driven re-renders from redrawing the whole log list.
  prev.job?.status === next.job?.status &&
  prev.job?.phase === next.job?.phase &&
  prev.job?.rows === next.job?.rows &&
  (prev.job?.logs?.length || 0) === (next.job?.logs?.length || 0) &&
  (prev.job?.timings?.total_ms || 0) === (next.job?.timings?.total_ms || 0)
);

/**
 * Compact cell that shows the latest scheduler-generated leads file for a
 * campaign, plus admin controls to regenerate on demand. See
 * `backend/app/leads_scheduler.py` for the 03:00-IST-daily background job.
 */
function DailyFileCell({ campaign, onRegenerated }) {
  const { user } = useAuth();
  const isAdmin = !!(user?.is_admin || user?.is_super_admin);
  const [regenerating, setRegenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const latest = campaign.latest_file;

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get(
        `/leads/campaigns/${campaign.id}/latest-file/download`,
        { responseType: "blob" },
      );
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const fname = m ? m[1] : `leads_${campaign.name}_${latest?.run_date || "latest"}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${(latest?.row_count || 0).toLocaleString()} leads`);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const regenerate = async () => {
    if (!isAdmin) return;
    setRegenerating(true);
    try {
      const r = await api.post(`/leads/campaigns/${campaign.id}/regenerate-now`);
      const res = (r.data?.results || [])[0];
      if (res?.status === "success") {
        toast.success(`Regenerated ${res.row_count?.toLocaleString?.() || 0} leads in ${res.duration_secs}s`);
      } else if (res?.status === "failed") {
        toast.error(res.error || "Regeneration failed");
      }
      await onRegenerated?.();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Regeneration failed");
    } finally {
      setRegenerating(false);
    }
  };

  if (!latest) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400 inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> No daily file yet
        </span>
        {isAdmin && (
          <button
            onClick={regenerate}
            disabled={regenerating}
            className="text-[10px] text-fuchsia-600 hover:text-fuchsia-800 underline underline-offset-2 disabled:opacity-40"
            data-testid={`leads-regenerate-${campaign.id}`}
          >
            {regenerating ? "Generating…" : "Generate now"}
          </button>
        )}
      </div>
    );
  }

  const isFailed = latest.status === "failed";
  const bytes = latest.size_bytes || 0;
  const sizeLabel = bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  const isZip = latest.file_ext === "zip";
  const partsLabel = isZip && latest.part_count > 1
    ? ` · zip (${latest.part_count} parts)`
    : "";

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {isFailed ? (
        <span className="text-xs text-red-600 inline-flex items-center gap-1"
              title={latest.error || ""}>
          <AlertTriangle className="w-3 h-3" /> {latest.run_date} · failed
        </span>
      ) : (
        <Button
          size="sm"
          onClick={download}
          disabled={downloading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 text-xs"
          data-testid={`leads-download-latest-${campaign.id}`}
          title={
            isZip
              ? `${latest.part_count} .xlsx parts packaged as a .zip because the export exceeded ${(50).toLocaleString()}k rows`
              : "Single .xlsx file"
          }
        >
          {downloading
            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Downloading…</>
            : <><Download className="w-3 h-3 mr-1" /> {latest.run_date} · {(latest.row_count || 0).toLocaleString()} · {sizeLabel}{partsLabel}</>}
        </Button>
      )}
      {isAdmin && (
        <button
          onClick={regenerate}
          disabled={regenerating}
          className="text-[10px] text-slate-500 hover:text-slate-800 underline underline-offset-2 disabled:opacity-40 inline-flex items-center gap-0.5"
          title="Regenerate this campaign's file now (admin only)"
          data-testid={`leads-regenerate-${campaign.id}`}
        >
          <RefreshCw className={`w-2.5 h-2.5 ${regenerating ? "animate-spin" : ""}`} />
          {regenerating ? "…" : "regen"}
        </button>
      )}
    </div>
  );
}


export default function LeadsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const reloadCampaigns = () => {
    return api.get("/leads/my-campaigns")
      .then((r) => setCampaigns(r.data?.campaigns || []))
      .catch((ex) => toast.error(ex?.response?.data?.detail || "Failed to load campaigns"));
  };

  useEffect(() => {
    reloadCampaigns().finally(() => setLoading(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const openGen = (c) => {
    setJob(null);
    setModal({ campaign: c, as_of_date: yesterdayIST(), dry_run: false });
  };

  const closeModal = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setModal(null);
    setJob(null);
  };

  const startExport = async () => {
    if (!modal?.campaign) return;
    setStarting(true);
    setJob(null);
    try {
      const r = await api.post(`/leads/campaigns/${modal.campaign.id}/leads-jobs`, {
        as_of_date: modal.as_of_date,
        dry_run: !!modal.dry_run,
        max_rows: 200000,
      });
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job_id returned");
      // Poll every 1s. Be resilient: tolerate up to 5 consecutive
      // network errors before giving up — one 504 on a slow proxy
      // used to kill the whole poller and freeze the modal.
      let consecutiveErrors = 0;
      const poll = async () => {
        try {
          const s = await api.get(`/leads/jobs/${jobId}`, { timeout: 30000 });
          consecutiveErrors = 0;
          setJob(s.data);
          if (s.data.status === "done" || s.data.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch (ex) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= 5) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            toast.error(`Lost connection to the job (5 failed polls). Job may still be running — reopen this dialog to reconnect.`);
          }
        }
      };
      await poll();  // immediate first tick
      pollRef.current = setInterval(poll, 1000);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not start export");
    } finally {
      setStarting(false);
    }
  };

  const downloadJob = async () => {
    if (!job?.id) return;
    try {
      const r = await api.get(`/leads/jobs/${job.id}/download`, { responseType: "blob" });
      const filename = (() => {
        const cd = r.headers["content-disposition"] || "";
        const m = cd.match(/filename="?([^";]+)"?/i);
        return m ? m[1] : `leads_${modal.campaign.name}_${modal.as_of_date}.xlsx`;
      })();
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${(job.rows ?? 0).toLocaleString()} row${job.rows === 1 ? "" : "s"}`);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Download failed");
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="leads-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Generate Leads</h1>
        <p className="text-sm text-slate-500 mt-1">
          Download an inactive-users file for any campaign you&apos;re assigned to.
          Progress logs are shown live while your export runs.
        </p>
      </div>

      {loading ? (
        <div className="glass-card p-8 text-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading campaigns&hellip;
        </div>
      ) : campaigns.length === 0 ? (
        <div className="glass-card p-8 text-center text-slate-500">
          You have no campaign assignments. Ask an admin to add you to a campaign.
        </div>
      ) : (
        <div className="glass-card p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-campaigns-table">
            <thead>
              <tr className="text-slate-500 text-xs uppercase">
                <th className="text-left py-2 px-3">Campaign</th>
                <th className="text-left py-2 px-3">Brand</th>
                <th className="text-left py-2 px-3">Team</th>
                <th className="text-right py-2 px-3">Min inactive</th>
                <th className="text-right py-2 px-3">Lookback</th>
                <th className="text-right py-2 px-3">Promo rules</th>
                <th className="text-left py-2 px-3">Daily file (auto)</th>
                <th className="text-right py-2 px-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="border-t border-white/60 hover:bg-white/50"
                    data-testid={`leads-campaign-row-${c.id}`}>
                  <td className="py-2 px-3 font-mono font-medium">{c.name}</td>
                  <td className="py-2 px-3">{c.brand_name}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{c.team_name || "—"}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">
                    {c.min_inactive_days ? `${c.min_inactive_days}d` : <span className="text-slate-400">0</span>}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-xs">
                    {c.max_lookback_days ? `${c.max_lookback_days}d` : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${
                      c.promo_rule_count > 0
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-amber-100 text-amber-800"
                    }`}>
                      <Tag className="w-3 h-3" /> {c.promo_rule_count}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <DailyFileCell campaign={c} onRegenerated={() => reloadCampaigns()} />
                  </td>
                  <td className="py-2 px-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => openGen(c)}
                            data-testid={`leads-generate-${c.id}`}>
                      Generate custom
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Generate modal.
          NB: intentionally NOT using `glass-card` here. The animated leaf
          background behind any non-opaque parent was leaking into repaints
          of child elements (calendar icon, toggle) as continuous 1-2px
          sub-pixel jitter. Solid white with `isolation: isolate` +
          `contain: paint` puts this modal in its own compositor layer so
          background animation cannot bleed into its repaints. See
          2026-07-09 "popup jitter" incident. */}
      <Dialog open={!!modal} onOpenChange={(v) => !v && closeModal()}>
        <DialogContent
          className="max-w-2xl border border-slate-200 shadow-2xl"
          style={{
            background: "#ffffff",
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
            isolation: "isolate",
            contain: "paint",
          }}
          data-testid="leads-generate-dialog"
        >
          <DialogHeader>
            <DialogTitle>Generate leads · <span className="font-mono">{modal?.campaign?.name}</span></DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="as-of">As-of date</Label>
              <div className="mt-1">
                <DatePicker
                  value={modal?.as_of_date || ""}
                  onChange={(v) => setModal({ ...modal, as_of_date: v })}
                  placeholder="Pick as-of date"
                  testId="leads-as-of-date"
                />
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                Defaults to yesterday IST. Inactive users are computed against this date;
                promo codes come from the config that was active on this date.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-slate-500">Min inactive:</span>{" "}
                <span className="font-mono">{modal?.campaign?.min_inactive_days ?? 0}d</span>
              </div>
              <div>
                <span className="text-slate-500">Lookback:</span>{" "}
                <span className="font-mono">
                  {modal?.campaign?.max_lookback_days ? `${modal.campaign.max_lookback_days}d` : "unbounded"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t">
              <Switch
                checked={!!modal?.dry_run}
                onCheckedChange={(v) => setModal({ ...modal, dry_run: v })}
                disabled={!!job && job.status !== "failed" && job.status !== "done"}
                data-testid="leads-dry-run-toggle"
              />
              <Label className="!m-0 text-xs">
                Dry-run <span className="text-slate-400 font-normal">(returns 3 fake rows, skips MySQL)</span>
              </Label>
            </div>

            {job && <MemoLogPanel job={job} />}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeModal}>
              {job?.status === "done" ? "Close" : "Cancel"}
            </Button>
            {(!job || job.status === "failed") && (
              <Button onClick={startExport} disabled={starting} data-testid="leads-start-btn">
                {starting ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Starting…</>
                ) : job?.status === "failed" ? (
                  <><Download className="w-3 h-3 mr-1" /> Retry</>
                ) : (
                  <><Download className="w-3 h-3 mr-1" /> Start export</>
                )}
              </Button>
            )}
            {job?.status === "done" && (
              <Button onClick={downloadJob} data-testid="leads-download-btn"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Download className="w-3 h-3 mr-1" /> Download .xlsx
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
